import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import "primereact/resources/themes/lara-light-blue/theme.css";
import Layout from "../../component/layout";
import ConfirmDialog from "../../component/confirmDialog";
import { getItem } from "../../../../utils/localStorage";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";
import {
  addLevyType,
  editLevyType,
  getLevyTypes,
  getLevies,
  editLevy,
  deleteLevy,
  disableLevy,
  facilityReminders,
  facilityPenalties,
  getGLAccountsURL,
  GET_CURRENCIES_URL,
  fetchFacilityPaymentDetails,
  getBankDetailsURL,
  getBillerAddressesURL,
  getDigitaxConfigURL
} from "../../../../utils/urls";

const INVOICE_DAY_OPTIONS = [
  { value: "5days", label: "5 days to end of month" },
  { value: "3days", label: "3 days to end of month" },
  { value: "1day", label: "1 day to end of month" },
  { value: "endmonth", label: "End of month" }
];

const DUE_DATE_OPTIONS = [
  { value: "1st", label: "1st of next month" },
  { value: "5th", label: "5th of next month" },
  { value: "10th", label: "10th of next month" }
];

const COLLECTION_FREQUENCY_OPTIONS = [
  { value: "Weekly", label: "Weekly" },
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Semi-annually", label: "Semi-annually" },
  { value: "Annually", label: "Annually" }
];

const NOTIFICATION_TYPES = [
  { value: "SMS", label: "SMS" },
  { value: "EMAIL", label: "Email" }
];

const OVERDUE_REMINDER_MODES = [
  { value: "specific", label: "Specific Days After Due Date" },
  { value: "daily", label: "Daily After Due Date" }
];

const OVERDUE_REMINDER_OPTIONS = [
  { value: 1, label: "1 day after due date" },
  { value: 3, label: "3 days after due date" },
  { value: 7, label: "7 days after due date" }
];

const LEVY_APPLICANT_OPTIONS = [
  { value: "home owner", label: "Home Owner" },
  { value: "tenant", label: "Tenant" }
];

const BILLING_TYPE_OPTIONS = [
  { value: "Prepaid", label: "Prepaid" },
  { value: "Postpaid", label: "Postpaid" }
];

function LevyManagement() {
  const navigate = useNavigate();

  // State declarations
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [isConfirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [levies, setLevies] = useState([]);
  const [filteredLevies, setFilteredLevies] = useState([]);
  const [levyType, setLevyType] = useState("");
  const [levyTypes, setLevyTypes] = useState([]);
  const [editingLevyType, setEditingLevyType] = useState(null);
  const [editedLevyTypeName, setEditedLevyTypeName] = useState("");
  const [collectionFrequency, setCollectionFrequency] = useState("");
  const [invoiceDay, setInvoiceDay] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [levyId, setLevyId] = useState(null);
  const [levyName, setLevyName] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedRowData, setSelectedRowData] = useState({});
  const [confirmAction, setConfirmAction] = useState("");
  const [selectedDisableStatus, setSelectedDisableStatus] = useState(false);
  const [selectedLevyId, setSelectedLevyId] = useState(null);
  const [levyApplicant, setLevyApplicant] = useState("");
  const [overdueDaily, setOverdueDaily] = useState(false);
  const [billingType, setBillingType] = useState("Postpaid");

  // Payment settings states
  const [currency, setCurrency] = useState("");
  const [mobilePayment, setMobilePayment] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [bankPayment, setBankPayment] = useState(false);
  const [bankAccountId, setBankAccountId] = useState("");
  const [billerAddressId, setBillerAddressId] = useState("");

  // Data states
  const [currencies, setCurrencies] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [billerAddresses, setBillerAddresses] = useState([]);

  // GL Account states
  const [glAccounts, setGlAccounts] = useState([]);
  const [glAccountsConfig, setGlAccountsConfig] = useState({
    invoice: {
      debit: "",
      credit: ""
    },
    payment: {
      debit: "",
      credit: ""
    }
  });
  const [loadingGLAccounts, setLoadingGLAccounts] = useState(false);

  // Reminder states
  const [reminderTime, setReminderTime] = useState("09:00");
  const [reminderInvoiceDate, setReminderInvoiceDate] = useState(true);
  const [reminderDueDate, setReminderDueDate] = useState(false);
  const [overdueEnabled, setOverdueEnabled] = useState(false);
  const [overdueDays, setOverdueDays] = useState([]);
  const [notificationTypes, setNotificationTypes] = useState(["SMS", "EMAIL"]);
  const [reminderMessage, setReminderMessage] = useState("");

  // Penalty states
  const [penaltyName, setPenaltyName] = useState("");
  const [penaltyType, setPenaltyType] = useState("");
  const [penaltyEffectDays, setPenaltyEffectDays] = useState("");
  const [penaltyPercentage, setPenaltyPercentage] = useState("");
  const [penaltyAmount, setPenaltyAmount] = useState("");
  const [penaltyActive, setPenaltyActive] = useState(true);

  const [digitaxEnabled, setDigitaxEnabled] = useState(false);
  const [digitaxProfileId, setDigitaxProfileId] = useState(null);
  const [digitaxSelection, setDigitaxSelection] = useState("");


  const [digitaxProfiles, setDigitaxProfiles] = useState([]);

  const fetchDigitaxProfiles = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getDigitaxConfigURL}/${facilityId}`,
        "GET"
      );

      console.log("Digitax config response:", response);

      if (response.success) {
        setDigitaxProfiles(
          response.data?.digitax?.profiles || []
        );
      }
    } catch (error) {
      console.error(
        "Failed to fetch DigiTax profiles:",
        error
      );
    }
  };

  // Fetch GL accounts
  const fetchGLAccounts = async () => {
    try {
      setLoadingGLAccounts(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        console.error("No facility ID found in localStorage");
        toastify("No facility selected. Please select a facility first.", "error");
        return;
      }

      const response = await makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET");

      if (response && response.success) {
        const accountsData = response.data?.data || [];
        setGlAccounts(accountsData);
      } else {
        const errorMsg = response?.message || "Failed to fetch GL accounts.";
        console.error("GL accounts API error:", errorMsg);
        toastify(errorMsg, "error");
        setGlAccounts([]);
      }
    } catch (error) {
      console.error("Error in fetchGLAccounts:", error);
      toastify("Error fetching GL accounts. Please try again later.", "error");
      setGlAccounts([]);
    } finally {
      setLoadingGLAccounts(false);
    }
  };

  // Fetch currencies
  const fetchCurrencies = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${GET_CURRENCIES_URL}/${facilityId}`, "GET");

      if (response.success) {
        let currencyData = [];

        if (Array.isArray(response.data)) {
          currencyData = response.data;
        } else if (response.data?.currencies && Array.isArray(response.data.currencies)) {
          currencyData = response.data.currencies;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          currencyData = response.data.data;
        } else if (typeof response.data === 'object' && response.data !== null) {
          currencyData = [response.data];
        }

        const processedCurrencies = currencyData.map(curr => ({
          _id: curr._id,
          code: curr.code || 'Unknown',
          name: curr.name || curr.currencyName || 'Unknown Currency'
        }));

        setCurrencies(processedCurrencies);
      } else {
        throw new Error(response.error || "Failed to fetch currencies");
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toastify(error.message || "Error fetching currencies", "error");
      setCurrencies([]);
    }
  };

  // Fetch payment settings
  const fetchPaymentSettings = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const url = `${fetchFacilityPaymentDetails}/${facilityId}`;
      const response = await makeRequest2(url, "GET");

      if (response.success && response.data?.length > 0) {
        const formattedData = response.data.map(item => ({
          shortCode: item.shortCode,
          module: item.module,
          _id: item._id
        }));
        setPaymentSettings(formattedData);
      } else {
        setPaymentSettings([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toastify("Error fetching payment details", "error");
      setPaymentSettings([]);
    }
  };

  // Fetch bank accounts
  const fetchBankAccounts = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getBankDetailsURL}/${facilityId}`, "GET");

      if (response.success) {
        const bankData = response.data?.data || response.data || [];
        setBankAccounts(bankData);
      } else {
        console.error("Failed to fetch bank details:", response.error);
        setBankAccounts([]);
        toastify(response.error || "Failed to fetch bank details", "error");
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      toastify("Error fetching bank accounts", "error");
      setBankAccounts([]);
    }
  };

  // Fetch biller addresses
  const fetchBillerAddresses = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getBillerAddressesURL}/${facilityId}`, "GET");

      if (response.success) {
        const billerData = response.data?.data || response.data || [];
        setBillerAddresses(billerData);
      } else {
        console.error("Failed to fetch biller addresses:", response.error);
        setBillerAddresses([]);
        toastify(response.error || "Failed to fetch biller addresses", "error");
      }
    } catch (error) {
      console.error("Error fetching biller addresses:", error);
      toastify("Error fetching biller addresses", "error");
      setBillerAddresses([]);
    }
  };

  // Check for levy types before navigation
  const checkLevyTypesBeforeNavigation = () => {
    if (!levyTypes || levyTypes.length === 0) {
      toastify("Please add at least one levy type before creating a levy", "warn");
      document.getElementById('profile-tab-5').click();
      return;
    }
    navigate("/facility/levy_management/levies/add_levy");
  };

  const handleEditLevyType = async (levyTypeId) => {
    try {
      const facilityId = await getItem('selectedFacilityId');

      const response = await makeRequest2(
        `${editLevyType}/${facilityId}/${levyTypeId}`,
        'PUT',
        { name: editedLevyTypeName }
      );

      if (response.success) {
        toastify("Levy Type updated successfully", 'success');
        setEditingLevyType(null);
        setEditedLevyTypeName("");
        fetchLevyTypes();
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      toastify(err.message, 'error');
    }
  };

  const handleInputSearch = (value) => {
    setSearchTerm(value);
    if (!value) {
      setFilteredLevies(levies);
      return;
    }
    const filtered = levies.filter((levy) =>
      Object.values(levy).some((field) =>
        String(field).toLowerCase().includes(value.toLowerCase())
      )
    );
    setFilteredLevies(filtered);
  };

  const fetchLevyTypes = async () => {
    try {
      const facilityId = await getItem('selectedFacilityId');
      const response = await makeRequest2(getLevyTypes + '/' + facilityId, 'GET', {});

      if (response.success) {
        const levyTypesData = Array.isArray(response.data) ? response.data :
          (response.data?.data ? response.data.data : []);
        setLevyTypes(levyTypesData);
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      console.error('Error fetching levy types:', err);
      toastify(err.message, 'error');
    }
  };

  const LevyTypeSubmit = async () => {
    try {
      if (!levyType.trim()) {
        toastify("Please enter a levy type name", "warning");
        return;
      }

      const facilityId = await getItem("selectedFacilityId");
      const levyTypeData = levyType;

      const response = await makeRequest2(
        addLevyType + "/" + facilityId,
        "POST",
        levyTypeData
      );
      if (response.success) {
        toastify("Levy type added successfully", "success");
        setLevyType("");
        fetchLevyTypes();
        // Switch to levy list tab
        document.getElementById('profile-tab-1').click();
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      toastify(err.message, "error");
    }
  };

  const fetchLevies = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");

      // Fetch levies, reminders and penalties in parallel
      const [leviesResponse, remindersResponse, penaltiesResponse] = await Promise.all([
        makeRequest2(`${getLevies}/${facilityId}`, "GET", {}),
        makeRequest2(`${facilityReminders}/${facilityId}/levy`, "GET", {}),
        makeRequest2(`${facilityPenalties}/${facilityId}/levy`, "GET", {})
      ]);

      console.log("Levies response:", leviesResponse);

      // Handle the levies response
      if (!leviesResponse.success) {
        throw new Error(leviesResponse.error || 'Failed to fetch levies');
      }

      // Get the levies data
      const leviesData = leviesResponse.data?.data || leviesResponse.data || [];
      const dataArray = Array.isArray(leviesData) ? leviesData : [];

      // Get reminders and penalties, ensuring we get arrays
      const reminders = Array.isArray(remindersResponse.data?.data) ? remindersResponse.data.data :
        Array.isArray(remindersResponse.data) ? remindersResponse.data : [];

      const penalties = Array.isArray(penaltiesResponse.data?.data) ? penaltiesResponse.data.data :
        Array.isArray(penaltiesResponse.data) ? penaltiesResponse.data : [];

      // Map reminders and penalties to levies
      const leviesWithAssociations = dataArray.map(levy => {
        const associatedReminder = reminders.find(reminder =>
          reminder?.moduleId &&
          reminder.moduleId.toString() === levy._id.toString()
        );

        const associatedPenalty = penalties.find(penalty =>
          penalty?.moduleId &&
          penalty.moduleId.toString() === levy._id.toString()
        );

        return {
          ...levy,
          levyType: typeof levy.levyType === 'object' ? levy.levyType?.name : levy.levyType,
          reminder: associatedReminder || null,
          penalty: associatedPenalty || null
        };
      });

      setLevies(leviesWithAssociations);
      setFilteredLevies(leviesWithAssociations);

    } catch (err) {
      console.error('Error fetching levies:', err);
      toastify(err.message, "error");
      setLevies([]);
      setFilteredLevies([]);
    }
  };

  const actionBodyTemplate = (rowData) => {
    const isDisabled = rowData.disabled;

    return (
      <div className="actions">
        <Button
          icon="ti ti-edit"
          className="p-button-rounded p-button-info mr-2"
          onClick={() => handleEdit(rowData)}
          tooltip="Edit"
        />
        <Button
          icon={`ti ${isDisabled ? "ti-lock-open" : "ti-lock"}`}
          className="p-button-rounded p-button-warning"
          onClick={() => {
            setSelectedLevyId(rowData._id);
            setConfirmAction(isDisabled ? "enable" : "disable");
            setSelectedDisableStatus(isDisabled);
            setConfirmDialogVisible(true);
          }}
          tooltip={isDisabled ? "Enable" : "Disable"}
        />
      </div>
    );
  };

  const handleEdit = (rowData) => {
    try {
      // Basic levy information
      setLevyId(rowData._id);
      setLevyName(rowData.levyName || "");
      setAmount(rowData.amount?.toString() || "");
      setCollectionFrequency(rowData.collectionFrequency || "");
      setInvoiceDay(rowData.invoiceDay || "");
      setDueDate(rowData.dueDate || "");
      setLevyApplicant(rowData.levyApplicant || "");
      setBillingType(rowData.billingType || "Postpaid");

      // Payment settings
      setCurrency(rowData.currency || "");
      setMobilePayment(rowData.mobilePayment || false);
      setPaymentMethodId(rowData.paymentMethodId || "");
      setBankPayment(rowData.bankPayment || false);
      setBankAccountId(rowData.bankAccountId || "");
      setBillerAddressId(rowData.billerAddressId || "");

      // DigiTax settings
      const digitaxData = rowData.digitax;

      console.log("[EDIT LEVY] DigiTax data:", digitaxData);
      console.log("[EDIT LEVY] Available profiles:", digitaxProfiles);

      if (digitaxData?.enabled) {

        setDigitaxEnabled(true);

        if (digitaxData.profileId) {

          const profileId = digitaxData.profileId.toString();

          console.log(
            "[EDIT LEVY] Existing DigiTax profile:",
            profileId
          );

          setDigitaxProfileId(profileId);
          setDigitaxSelection(profileId);

        } else {

          console.log(
            "[EDIT LEVY] Existing DigiTax uses facility configuration"
          );

          setDigitaxProfileId(null);
          setDigitaxSelection("legacy");
        }

      } else {

        console.log(
          "[EDIT LEVY] DigiTax is disabled"
        );

        setDigitaxEnabled(false);
        setDigitaxProfileId(null);
        setDigitaxSelection("");
      }

      // GL Accounts settings
      if (rowData.glAccounts) {
        setGlAccountsConfig({
          invoice: {
            debit: rowData.glAccounts.invoice?.debit || "",
            credit: rowData.glAccounts.invoice?.credit || ""
          },
          payment: {
            debit: rowData.glAccounts.payment?.debit || "",
            credit: rowData.glAccounts.payment?.credit || ""
          }
        });
      } else {
        setGlAccountsConfig({
          invoice: { debit: "", credit: "" },
          payment: { debit: "", credit: "" }
        });
      }

      // Extract levy type ID with careful checks
      let levyTypeData;
      if (rowData.levyType) {
        if (typeof rowData.levyType === 'object') {
          levyTypeData = rowData.levyType;
        } else {
          // Find the levy type from the levy types list
          const foundLevyType = levyTypes.find(lt => lt.name === rowData.levyType);
          levyTypeData = foundLevyType;
        }
      }

      if (!levyTypeData) {
        console.error('Could not find valid levy type');
        toastify('Error: Invalid levy type', 'error');
        return;
      }

      // Handle reminder settings
      const reminderData = rowData.reminder;
      if (reminderData) {
        setReminderTime(reminderData.time || "09:00");
        setReminderInvoiceDate(reminderData.remindOn?.invoiceDate ?? true);
        setReminderDueDate(reminderData.remindOn?.dueDate ?? false);
        setOverdueEnabled(reminderData.remindOn?.afterOverdue?.enabled ?? false);

        // Set daily mode if applicable
        setOverdueDaily(reminderData.remindOn?.afterOverdue?.daily ?? false);

        // Ensure overdueDays is always an array
        const days = reminderData.remindOn?.afterOverdue?.days || [];
        setOverdueDays(Array.isArray(days) ? days : []);

        // Ensure notificationTypes is always an array
        const types = reminderData.notificationTypes || [];
        setNotificationTypes(Array.isArray(types) ? types : ["SMS", "EMAIL"]);

        setReminderMessage(reminderData.message || "");
      } else {
        // Set default reminder values
        setReminderTime("09:00");
        setReminderInvoiceDate(true);
        setReminderDueDate(false);
        setOverdueEnabled(false);
        setOverdueDaily(false);
        setOverdueDays([]);
        setNotificationTypes(["SMS", "EMAIL"]);
        setReminderMessage("");
      }

      // Handle penalty settings
      const penaltyData = rowData.penalty;
      if (penaltyData) {
        // Make sure we're setting string values for form fields
        setPenaltyName(penaltyData.name || "");
        setPenaltyType(penaltyData.type || "");

        // For numeric fields, ensure we're setting them as strings for form inputs
        setPenaltyEffectDays(penaltyData.effectDays?.toString() || "");

        // Handle percentage based on penalty type
        if (penaltyData.type === 'percentage') {
          setPenaltyPercentage(penaltyData.percentage?.toString() || "");
          setPenaltyAmount(""); // Clear the other field
        }
        // Handle fixed amount based on penalty type
        else if (penaltyData.type === 'fixed') {
          setPenaltyAmount(penaltyData.amount?.toString() || "");
          setPenaltyPercentage(""); // Clear the other field
        }
        // If no type is specified, clear both fields
        else {
          setPenaltyPercentage("");
          setPenaltyAmount("");
        }

        // Set the active state
        setPenaltyActive(penaltyData.isActive !== false); // Default to true if not specified
      } else {
        // Set default penalty values
        setPenaltyName("");
        setPenaltyType("");
        setPenaltyEffectDays("");
        setPenaltyPercentage("");
        setPenaltyAmount("");
        setPenaltyActive(true);
      }

      // Store complete row data
      setSelectedRowData({
        ...rowData,
        levyType: levyTypeData,
        reminder: reminderData || null,
        penalty: penaltyData || null
      });

      setEditDialogVisible(true);
    } catch (error) {
      console.error('Error in handleEdit:', error);
      toastify('Error preparing levy data for editing', 'error');
    }
  };

  const handleConfirmAction = async () => {
    setConfirmDialogVisible(false);
    if (confirmAction === "enable" || confirmAction === "disable") {
      await handleDisable(selectedLevyId, selectedDisableStatus);
    }
  };

  const HandleEditSubmit = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");

      const validationError = validateLevyForm();
      if (validationError) {
        toastify(validationError, "error");
        return;
      }

      // Construct reminder document
      const reminderPayload = {
        name: `${levyName} Reminder`,
        type: 'standard',
        module: 'levy',
        moduleId: levyId, // This is the specific levy's ID
        remindOn: {
          invoiceDate: reminderInvoiceDate,
          dueDate: reminderDueDate,
          afterOverdue: {
            enabled: overdueEnabled,
            daily: overdueDaily,
            days: overdueDaily ? [] : overdueDays // Only include days when not daily
          }
        },
        time: reminderTime || '09:00',
        isActive: true,
        processed: false,
        notificationTypes: notificationTypes,
        message: reminderMessage || '',
        facilityId: facilityId,
        timezone: "UTC"
      };

      // Construct penalty document
      const penaltyPayload = penaltyType ? {
        name: penaltyName,
        type: penaltyType,
        effectDays: penaltyEffectDays ? parseInt(penaltyEffectDays) : 0,
        percentage: penaltyType === 'percentage' ? (penaltyPercentage ? parseFloat(penaltyPercentage) : 0) : null,
        amount: penaltyType === 'fixed' ? (penaltyAmount ? parseFloat(penaltyAmount) : 0) : null,
        module: 'levy',
        moduleId: levyId,
        isActive: penaltyActive,
        facilityId: facilityId
      } : null;

      const updatedData = {
        levyName,
        levyType: selectedRowData.levyType._id, // Just the levy type ID
        amount: parseFloat(amount),
        collectionFrequency,
        invoiceDay,
        dueDate,
        levyApplicant,
        billingType,
        // Payment settings
        currency,
        mobilePayment,
        paymentMethodId: mobilePayment ? paymentMethodId : null,
        bankPayment,
        bankAccountId: bankPayment ? bankAccountId : null,
        billerAddressId,
        digitaxEnabled,
        digitaxProfileId,
        // GL Accounts
        glAccounts: glAccountsConfig,
        reminder: reminderPayload,
        penalty: penaltyPayload,
        reminderId: selectedRowData.reminder?._id,
        penaltyId: selectedRowData.penalty?._id
      };

      console.log("Sending updated data:", updatedData); // For debugging

      const response = await makeRequest2(
        `${editLevy}/${facilityId}/${levyId}`,
        "PUT",
        updatedData
      );

      if (response.success) {
        toastify("Levy edited successfully", "success");
        handleClear();
        setEditDialogVisible(false);
        fetchLevies();
      } else {
        throw new Error(response.error || 'Failed to update levy');
      }
    } catch (err) {
      console.error('Error in HandleEditSubmit:', err);
      toastify(err.message, "error");
    }
  };

  const validateLevyForm = () => {
    if (!levyName.trim()) return "Levy name is required";
    if (!amount || parseFloat(amount) <= 0) return "Valid amount is required";
    if (!collectionFrequency) return "Collection frequency is required";
    if (!invoiceDay) return "Invoice day is required";
    if (!dueDate) return "Due date is required";
    if (!levyApplicant) return "Levy applicant is required";
    if (!billingType) return "Billing type is required";
    if (!currency) return "Currency is required";
    if (!billerAddressId) return "Biller address is required";

    // Payment method validation
    if (mobilePayment && !paymentMethodId) return "Payment method is required when mobile payment is enabled";
    if (bankPayment && !bankAccountId) return "Bank account is required when bank payment is enabled";
    if (!mobilePayment && !bankPayment) return "At least one payment method must be enabled";

    if (penaltyType) {
      if (!penaltyName) return "Penalty name is required";

      const effectDaysValue = parseInt(penaltyEffectDays);
      if (isNaN(effectDaysValue) || effectDaysValue < 0) return "Valid effect days are required";

      if (overdueEnabled) {
        // Only validate days when not in daily mode
        if (!overdueDaily && (!overdueDays || overdueDays.length === 0)) {
          return "Please select at least one day for overdue reminders";
        }
      }

      if (penaltyType === 'percentage') {
        const percentageValue = parseFloat(penaltyPercentage);
        if (isNaN(percentageValue) || percentageValue <= 0 || percentageValue > 100)
          return "Valid penalty percentage is required (1-100)";
      } else if (penaltyType === 'fixed') {
        const amountValue = parseFloat(penaltyAmount);
        if (isNaN(amountValue) || amountValue <= 0)
          return "Valid penalty amount is required";
      }
    }

    return null;
  };

  const handleDisable = async (levyId, currentStatus) => {
    const action = currentStatus ? "enable" : "disable";

    try {
      const facilityId = await getItem("selectedFacilityId");
      const newDisabledStatus = !currentStatus;

      const response = await makeRequest2(
        `${disableLevy}/${facilityId}/${levyId}`,
        "POST",
        { disabled: newDisabledStatus }
      );

      if (response.success) {
        toastify(`Levy ${action}d successfully`, "success");
        fetchLevies();
      } else {
        throw new Error(response.error || `Failed to ${action} levy`);
      }
    } catch (err) {
      toastify(err.message || `An error occurred while trying to ${action} the levy`, "error");
    }
  };

  const handleClear = () => {
    setLevyName("");
    setAmount("");
    setCollectionFrequency("");
    setInvoiceDay("");
    setDueDate("");
    setLevyApplicant("");
    setBillingType("Postpaid");

    // Clear payment settings
    setCurrency("");
    setMobilePayment(false);
    setPaymentMethodId("");
    setBankPayment(false);
    setBankAccountId("");
    setBillerAddressId("");

    // Clear GL accounts
    setGlAccountsConfig({
      invoice: { debit: "", credit: "" },
      payment: { debit: "", credit: "" }
    });

    // Clear reminder settings
    setReminderTime("09:00");
    setReminderInvoiceDate(true);
    setReminderDueDate(false);
    setOverdueEnabled(false);
    setOverdueDays([]);
    setOverdueDaily(false);
    setNotificationTypes(["SMS", "EMAIL"]);
    setReminderMessage("");

    // Clear penalty settings
    setPenaltyName("");
    setPenaltyType("");
    setPenaltyEffectDays("");
    setPenaltyPercentage("");
    setPenaltyAmount("");
    setPenaltyActive(true);
  };

  const statusTemplate = (rowData) => {
    const isDisabled = rowData.disabled;
    return (
      <span
        style={{
          fontWeight: "bold",
          color: isDisabled ? "#ff4d4d" : "#4caf50",
          padding: "5px 10px",
          borderRadius: "15px",
          backgroundColor: isDisabled ? "#ffebeb" : "#e8f5e9",
        }}
      >
        {isDisabled ? "Disabled" : "Enabled"}
      </span>
    );
  };

  useEffect(() => {
    fetchDigitaxProfiles();
    fetchLevyTypes();
    fetchLevies();
    fetchCurrencies();
    fetchPaymentSettings();
    fetchBankAccounts();
    fetchBillerAddresses();
    fetchGLAccounts();
  }, []);

  return (
    <Layout>
      {/* Header Section */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={"/facility/"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={""}>Levy Management</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="card">
        <div className="card-body py-0">
          <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
            <li className="nav-item">
              <a
                className="nav-link active"
                id="profile-tab-1"
                data-bs-toggle="tab"
                href="#profile-1"
                role="tab"
                aria-selected="true"
              >
                <i className="ti ti-chart-pie me-2"></i>Levy List
              </a>
            </li>
            <li className="nav-item">
              <a
                className="nav-link"
                id="profile-tab-5"
                data-bs-toggle="tab"
                href="#profile-5"
                role="tab"
                aria-selected="true"
              >
                <i className="ti ti-book me-2"></i>Levy Types
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Levy List Tab */}
        <div
          className="tab-pane show active"
          id="profile-1"
          role="tabpanel"
          aria-labelledby="profile-tab-1"
        >
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header">
                  <h5>Levies</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-12 col-xxl-12">
                      <div className="card">
                        <div className="card-body position-relative">
                          <div className="row">
                            <div className="col-md-3 col-xs-12 mb-3">
                              <input
                                className="form-control"
                                placeholder="Search here"
                                value={searchTerm}
                                onChange={(e) => handleInputSearch(e.target.value)}
                              />
                            </div>
                            <div className="col-md-9 col-xs-12 mb-3">
                              <div style={{ float: "right" }}>
                                <button
                                  className="btn"
                                  style={{ backgroundColor: 'limegreen', color: 'white', border: 'none' }}
                                  onClick={checkLevyTypesBeforeNavigation}
                                >
                                  Add Levy
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-12">
                              <div className="dt-responsive table-responsive">
                                <DataTable
                                  value={filteredLevies}
                                  emptyMessage="No levies found."
                                  sortMode="multiple"
                                  paginator
                                  rows={10}
                                  stripedRows
                                  tableStyle={{ minWidth: "50rem" }}
                                  scrollable
                                >
                                  <Column field="levyName" header="Name" />
                                  <Column field="levyType" header="Levy Type" />
                                  <Column field="amount" header="Amount" />
                                  <Column field="levyApplicant" header="Applicant" />
                                  <Column field="collectionFrequency" header="Frequency" />
                                  <Column field="dueDate" header="Due Date" />
                                  <Column field="billingType" header="Billing Type" />
                                  <Column field="digitax.enabled" header="Digitax Sync" />
                                  <Column body={statusTemplate} header="Status" />
                                  <Column body={actionBodyTemplate} header="Actions" />
                                </DataTable>
                                <ConfirmDialog
                                  visible={isConfirmDialogVisible}
                                  onHide={() => setConfirmDialogVisible(false)}
                                  message={`Are you sure you want to ${confirmAction} this Levy?`}
                                  onConfirm={handleConfirmAction}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Levy Types Tab */}
      <div
        className="tab-pane fade"
        id="profile-5"
        role="tabpanel"
        aria-labelledby="profile-tab-5"
      >
        <div className="card">
          <div className="card-header">
            <h5>Levy Types</h5>
          </div>
          <div className="tab-content">
            <div className="tab-pane active" id="setting-1" role="tabpanel">
              <div className="row p-10 mt-3">
                <div className="col-lg-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="mt-2">
                        <label>Add New Levy Type:</label>
                        <div style={{
                          backgroundColor: '#f0fdf4',
                          padding: '1rem',
                          fontSize: '0.875rem',
                          fontStyle: 'italic',
                          color: '#4b5563',
                          borderRadius: '0.5rem'
                        }}>
                          Levy types help categorize different kinds of levies (e.g., Service Charge, Maintenance Fee, Security Fee).
                          These categories must be set up before creating specific levies.
                        </div>
                        <input
                          className="form-control mt-2"
                          type="text"
                          value={levyType}
                          placeholder="Enter levy type name"
                          onChange={(e) => {
                            setLevyType(e.target.value);
                          }}
                        />
                      </div>

                      <div className="mt-3">
                        <button
                          className="btn"
                          style={{ backgroundColor: 'limegreen', color: 'white', border: 'none' }}
                          onClick={LevyTypeSubmit}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="card">
                    <div className="card-header">
                      <h6>Existing Levy Types</h6>
                    </div>
                    <div className="card-body">
                      <ul className="list-group">
                        {levyTypes && levyTypes.length > 0 ? (
                          levyTypes.map((levyType, index) => (
                            <li className="list-group-item d-flex justify-content-between align-items-center" key={index}>
                              {editingLevyType === levyType._id ? (
                                <div className="d-flex w-100">
                                  <input
                                    className="form-control me-2"
                                    type="text"
                                    value={editedLevyTypeName}
                                    onChange={(e) => setEditedLevyTypeName(e.target.value)}
                                  />
                                  <button
                                    className="btn btn-outline-secondary mx-1"
                                    onClick={() => {
                                      setEditingLevyType(null);
                                      setEditedLevyTypeName("");
                                    }}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    className="btn btn-primary"
                                    onClick={() => handleEditLevyType(levyType._id)}
                                  >
                                    Save
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <span>{index + 1}. {levyType.name}</span>
                                  <button
                                    className="btn btn-link p-0"
                                    onClick={() => {
                                      setEditingLevyType(levyType._id);
                                      setEditedLevyTypeName(levyType.name);
                                    }}
                                  >
                                    <i className="ti ti-edit f-18 text-primary"></i>
                                  </button>
                                </>
                              )}
                            </li>
                          ))
                        ) : (
                          <li className="list-group-item text-center">
                            <i className="ti ti-info-circle me-2"></i>
                            No levy types found. Add your first levy type to get started.
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Levy Dialog */}
      <Dialog
        header="Edit Levy"
        style={{ width: "70vw", maxHeight: "90vh" }}
        visible={editDialogVisible}
        onHide={() => {
          if (!editDialogVisible) return;
          setEditDialogVisible(false);
          handleClear();
        }}
      >
        <div className="col-md-12 mt-3" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {/* Basic Levy Information */}
          <div className="card p-3 mb-4 border-0 shadow-sm">
            <h6 className="mb-3 text-primary">Basic Information</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Name<span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  type="text"
                  placeholder="Levy Name"
                  value={levyName}
                  onChange={(e) => setLevyName(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Amount<span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  type="number"
                  placeholder="Levy Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Levy Applicant<span className="text-danger">*</span> <em>(who pays)</em>
                </label>
                <select
                  className="form-control"
                  value={levyApplicant}
                  onChange={(e) => setLevyApplicant(e.target.value)}
                >
                  <option value="">Select</option>
                  {LEVY_APPLICANT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Billing Type<span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={billingType}
                  onChange={(e) => setBillingType(e.target.value)}
                >
                  {BILLING_TYPE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  {billingType === 'Prepaid'
                    ? 'Customers pay before services are provided'
                    : 'Customers pay after services are provided'
                  }
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Collection Frequency<span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={collectionFrequency}
                  onChange={(e) => setCollectionFrequency(e.target.value)}
                >
                  <option value="">Select</option>
                  {COLLECTION_FREQUENCY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Invoice Day<span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={invoiceDay}
                  onChange={(e) => setInvoiceDay(e.target.value)}
                >
                  <option value="">Select</option>
                  {INVOICE_DAY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Due Date<span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                >
                  <option value="">Select</option>
                  {DUE_DATE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Payment Settings */}
          <div className="card p-3 mb-4 border-0 shadow-sm">
            <h6 className="mb-3 text-primary">Payment Settings</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Currency<span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="">-- Select Currency --</option>
                  {currencies.map((curr) => (
                    <option key={curr._id} value={curr._id}>
                      {curr.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Biller Address<span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={billerAddressId}
                  onChange={(e) => setBillerAddressId(e.target.value)}
                >
                  <option value="">-- Select Biller Address --</option>
                  {billerAddresses.map((address) => (
                    <option key={address._id} value={address._id}>
                      {address.name} - {address.companyName}
                      {address.isDefault && ' (Default)'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Mobile Payment</label>
                <div className="d-flex align-items-center">
                  <i
                    className={`ti ${!mobilePayment
                      ? "ti-toggle-left text-danger"
                      : "ti-toggle-right text-primary"
                      }`}
                    style={{ fontSize: "34px", cursor: "pointer" }}
                    onClick={() => setMobilePayment(!mobilePayment)}
                  />
                  <span className="ms-2">{mobilePayment ? "Enabled" : "Disabled"}</span>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Bank Payment</label>
                <div className="d-flex align-items-center">
                  <i
                    className={`ti ${!bankPayment
                      ? "ti-toggle-left text-danger"
                      : "ti-toggle-right text-primary"
                      }`}
                    style={{ fontSize: "34px", cursor: "pointer" }}
                    onClick={() => setBankPayment(!bankPayment)}
                  />
                  <span className="ms-2">{bankPayment ? "Enabled" : "Disabled"}</span>
                </div>
              </div>

              {mobilePayment && (
                <div className="col-md-6 mb-3">
                  <label className="form-label">Mobile Payment Method<span className="text-danger">*</span></label>
                  <select
                    className="form-control"
                    value={paymentMethodId}
                    onChange={(e) => setPaymentMethodId(e.target.value)}
                  >
                    <option value="">-- Select Payment Method --</option>
                    {paymentSettings.map((setting, index) => (
                      <option key={index} value={setting._id}>
                        {setting.module} - {setting.shortCode}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {bankPayment && (
                <div className="col-md-6 mb-3">
                  <label className="form-label">Bank Account<span className="text-danger">*</span></label>
                  <select
                    className="form-control"
                    value={bankAccountId}
                    onChange={(e) => setBankAccountId(e.target.value)}
                  >
                    <option value="">-- Select Bank Account --</option>
                    {bankAccounts.map((account) => (
                      <option key={account._id} value={account._id}>
                        {account.bankName} - {account.accountNumber} ({account.accountName})
                        {account.isDefault && ' (Default)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>


            <div className="col-md-6 mb-3">
              <label className="form-label">
                DigiTax Submission Profile
              </label>

              <select
                className="form-control"
                value={digitaxSelection}
                onChange={(e) => {
                  const value = e.target.value;

                  setDigitaxSelection(value);

                  if (value === "") {
                    setDigitaxEnabled(false);
                    setDigitaxProfileId(null);
                  } else if (value === "legacy") {
                    setDigitaxEnabled(true);
                    setDigitaxProfileId(null);
                  } else {
                    setDigitaxEnabled(true);
                    setDigitaxProfileId(value);
                  }
                }}
              >
                <option value="">
                  Do not submit to DigiTax
                </option>

                <option value="legacy">
                  Use Facility DigiTax Configuration
                </option>

                {digitaxProfiles
                  .filter(profile => profile.enabled)
                  .map(profile => (
                    <option
                      key={profile._id}
                      value={profile._id}
                    >
                      {profile.name}
                    </option>
                  ))}
              </select>

            </div>
          </div>

          {/* GL Accounts Section */}
          <div className="card p-3 mb-4 border-0 shadow-sm">
            <h6 className="mb-3 text-primary">GL Account Configuration</h6>
            {loadingGLAccounts && (
              <div className="alert alert-info">
                Loading GL accounts...
              </div>
            )}
            {!loadingGLAccounts && glAccounts.length === 0 && (
              <div className="alert alert-warning">
                No GL accounts found. Please create GL accounts first.
              </div>
            )}

            {glAccounts.length > 0 && (
              <>
                <div className="row mb-3">
                  <div className="col-12 mb-2">
                    <h6>Invoice GL Entries</h6>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label mb-2">Invoice Debit Account<span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={glAccountsConfig.invoice.debit}
                      onChange={(e) => setGlAccountsConfig(prev => ({
                        ...prev,
                        invoice: { ...prev.invoice, debit: e.target.value }
                      }))}
                    >
                      <option value="">-- Select Debit Account --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be debited when invoices are created</small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label mb-2">Invoice Credit Account<span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={glAccountsConfig.invoice.credit}
                      onChange={(e) => setGlAccountsConfig(prev => ({
                        ...prev,
                        invoice: { ...prev.invoice, credit: e.target.value }
                      }))}
                    >
                      <option value="">-- Select Credit Account --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be credited when invoices are created</small>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12 mb-2">
                    <h6>Payment GL Entries</h6>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label mb-2">Payment Debit Account<span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={glAccountsConfig.payment.debit}
                      onChange={(e) => setGlAccountsConfig(prev => ({
                        ...prev,
                        payment: { ...prev.payment, debit: e.target.value }
                      }))}
                    >
                      <option value="">-- Select Debit Account --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be debited when payments are received</small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label mb-2">Payment Credit Account<span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={glAccountsConfig.payment.credit}
                      onChange={(e) => setGlAccountsConfig(prev => ({
                        ...prev,
                        payment: { ...prev.payment, credit: e.target.value }
                      }))}
                    >
                      <option value="">-- Select Credit Account --</option>
                      {glAccounts.map((account) => (
                        <option key={account._id} value={account._id}>
                          {account.accountCode} - {account.accountName} ({account.accountType})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Account to be credited when payments are received</small>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Reminder Settings Section */}
          <div className="card p-3 mb-4 border-0 shadow-sm">
            <h6 className="mb-3 text-primary">Reminder Settings</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Reminder Time</label>
                <input
                  type="time"
                  className="form-control"
                  value={reminderTime}
                  onChange={(e) => setReminderTime(e.target.value)}
                />
                <small className="text-muted">Default: 9:00 AM</small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Send Reminders On</label>
                <div className="d-flex flex-column gap-2">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="invoiceDate"
                      checked={reminderInvoiceDate}
                      onChange={(e) => setReminderInvoiceDate(e.target.checked)}
                    />
                    <label className="form-check-label ms-2" htmlFor="invoiceDate">
                      Invoice Generation Date
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="dueDate"
                      checked={reminderDueDate}
                      onChange={(e) => setReminderDueDate(e.target.checked)}
                    />
                    <label className="form-check-label ms-2" htmlFor="dueDate">
                      Due Date
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Notification Methods</label>
                <div className="d-flex gap-3">
                  {NOTIFICATION_TYPES.map(type => (
                    <div key={type.value} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`notification-${type.value}`}
                        checked={notificationTypes.includes(type.value)}
                        onChange={() => {
                          setNotificationTypes(prev =>
                            prev.includes(type.value)
                              ? prev.filter(t => t !== type.value)
                              : [...prev, type.value]
                          );
                        }}
                      />
                      <label className="form-check-label ms-2" htmlFor={`notification-${type.value}`}>
                        {type.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Overdue Reminder</label>
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="overdueEnabled"
                    checked={overdueEnabled}
                    onChange={(e) => setOverdueEnabled(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="overdueEnabled">
                    Enable Overdue Reminder
                  </label>
                </div>

                {overdueEnabled && (
                  <>
                    <select
                      className="form-control mb-2 mt-2"
                      value={overdueDaily ? 'daily' : 'specific'}
                      onChange={(e) => setOverdueDaily(e.target.value === 'daily')}
                    >
                      {OVERDUE_REMINDER_MODES.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    {overdueEnabled && !overdueDaily && (
                      <div className="mt-2">
                        <label className="form-label">Select days after due date</label>
                        <div className="d-flex flex-column gap-2">
                          {OVERDUE_REMINDER_OPTIONS.map(option => (
                            <div key={option.value} className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`overdue-day-${option.value}`}
                                checked={overdueDays.includes(option.value)}
                                onChange={(e) => {
                                  const currentDays = [...overdueDays];
                                  if (e.target.checked) {
                                    if (!currentDays.includes(option.value)) {
                                      currentDays.push(option.value);
                                    }
                                  } else {
                                    const index = currentDays.indexOf(option.value);
                                    if (index !== -1) {
                                      currentDays.splice(index, 1);
                                    }
                                  }
                                  setOverdueDays(currentDays);
                                }}
                              />
                              <label className="form-check-label" htmlFor={`overdue-day-${option.value}`}>
                                {option.label}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {overdueEnabled && overdueDaily && (
                      <div className="mt-2">
                        <small className="text-muted">
                          A reminder will be sent every day after the due date until payment is made.
                        </small>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Reminder Message (Optional)</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                  maxLength="500"
                ></textarea>
                <small className="text-muted">
                  Maximum 500 characters. {reminderMessage ? `${500 - reminderMessage.length} characters remaining` : ''}
                </small>
              </div>
            </div>
          </div>

          {/* Penalty Settings Section */}
          <div className="card p-3 mb-4 border-0 shadow-sm">
            <h6 className="mb-3 text-primary">Penalty Settings</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Penalty Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={penaltyName}
                  onChange={(e) => setPenaltyName(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Penalty Type</label>
                <select
                  className="form-control"
                  value={penaltyType}
                  onChange={(e) => setPenaltyType(e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>

              {penaltyType === 'percentage' && (
                <div className="col-md-6 mb-3">
                  <label className="form-label">Percentage</label>
                  <input
                    type="number"
                    className="form-control"
                    value={penaltyPercentage}
                    onChange={(e) => setPenaltyPercentage(e.target.value)}
                    min="0"
                    max="100"
                    step="0.01"
                  />
                </div>
              )}

              {penaltyType === 'fixed' && (
                <div className="col-md-6 mb-3">
                  <label className="form-label">Fixed Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={penaltyAmount}
                    onChange={(e) => setPenaltyAmount(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
              )}

              <div className="col-md-6 mb-3">
                <label className="form-label">Effect Days</label>
                <input
                  type="number"
                  className="form-control"
                  value={penaltyEffectDays}
                  onChange={(e) => setPenaltyEffectDays(e.target.value)}
                  min="1"
                />
                <small className="text-muted">Number of days after which the penalty takes effect</small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="penaltyActive"
                    checked={penaltyActive}
                    onChange={(e) => setPenaltyActive(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="penaltyActive">
                    Active
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              className="btn btn-outline-secondary"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={HandleEditSubmit}
            >
              Save Changes
            </button>
          </div>
        </div>
      </Dialog>
    </Layout>
  );
}

export default LevyManagement;
